import { Button, Form, Grid, Popup, SectionItem } from '@mairistem/composants';
import { isNewIdentifiant } from '@mairistem/tools';
import _ from 'lodash';
import React from 'react';
import { REGISSEUR } from '@/constants';
import type { RegieRegisseurType } from '@/types';
import { formatUtilisateurToRegisseur } from '@/utils';
import { RegieMandataireListeTable, RegieRegisseurListeTable } from '../../Regisseur';
import { ModalRegisseurs } from '../../Regisseur/ModalRegisseur';

interface RegisseursProps {
  identifiant: string | number;
  // Régisseurs / régisseurs suppléant / agents de guichet
  regisseurs?: RegieRegisseurType[];
  regisseurConnexion?: boolean;
  onChange?: (e: any, shouldChangeStatus?: boolean) => void;
}

const Regisseurs = (props: RegisseursProps) => {
  const { identifiant, regisseurs, regisseurConnexion = false, onChange } = props;

  const [addModal, setAddModal] = React.useState(null);

  const handleCheckboxChange = React.useCallback(
    (e, { name, checked }) => {
      if (onChange) {
        onChange({
          identifiant,
          [name]: checked,
        });
      }
    },
    [identifiant, onChange],
  );

  const handleRegisseurAdd = React.useCallback(
    (utilisateur, mandat, actif) => {
      onChange?.({
        identifiant,
        regisseurs: [
          ...(regisseurs || []),
          {
            ...formatUtilisateurToRegisseur(utilisateur),
            mandat,
            actif,
          },
        ],
      });

      setAddModal(null);
    },
    [identifiant, onChange, regisseurs],
  );

  const handleRegisseurEdit = React.useCallback(
    (regisseur) => {
      onChange?.({
        identifiant,
        regisseurs: _.map(regisseurs, (r) =>
          r.identifiantLogin === regisseur.identifiantLogin
            ? {
                ...r,
                ...(isNewIdentifiant(regisseur.identifiant)
                  ? _.omit(regisseur, 'identifiant')
                  : regisseur),
              }
            : r,
        ),
      });
    },
    [identifiant, onChange, regisseurs],
  );

  /**
   * Suppression d'un régisseur en local uniquement
   * (qui n'est pas encore relié à la régie réèllement)
   */
  const handleRegisseurRemove = React.useCallback(
    (regisseur: RegieRegisseurType) => {
      onChange?.(
        {
          identifiant,
          regisseurs: _.filter(
            regisseurs,
            (r) => r.identifiantLogin !== regisseur.identifiantLogin,
          ),
        },
        false,
      );
    },
    [identifiant, onChange, regisseurs],
  );

  const allRegisseursWithoutJvs = React.useMemo(
    () => _.filter(regisseurs, ({ login }) => _.toLower(login) !== 'jvs'),
    [regisseurs],
  );

  const regisseursPrincipaux = React.useMemo(
    () => _.filter(allRegisseursWithoutJvs, ({ mandat }) => mandat === REGISSEUR.TYPE_PRINCIPAL),
    [allRegisseursWithoutJvs],
  );

  const regisseursMandataires = React.useMemo(
    () => _.filter(allRegisseursWithoutJvs, ({ mandat }) => mandat !== REGISSEUR.TYPE_PRINCIPAL),
    [allRegisseursWithoutJvs],
  );

  return (
    <Grid stackable relaxed>
      <Grid.Row style={{ paddingTop: 0 }}>
        <Grid.Column width={6} style={{ paddingLeft: 0 }}>
          <SectionItem title="Régisseurs" single compact>
            <RegieRegisseurListeTable
              onRegisseurEdit={handleRegisseurEdit}
              onRegisseurRemove={handleRegisseurRemove}
              regisseurs={regisseursPrincipaux}
            />
          </SectionItem>
        </Grid.Column>
        <Grid.Column width={1} />
        <Grid.Column width={8} style={{ paddingRight: 0 }}>
          <SectionItem
            title="Mandataires (régisseurs suppléants, agents de guichet)"
            single
            compact
          >
            <RegieMandataireListeTable
              onRegisseurEdit={handleRegisseurEdit}
              onRegisseurRemove={handleRegisseurRemove}
              mandataires={regisseursMandataires}
            />
          </SectionItem>
          {addModal && (
            <ModalRegisseurs
              onClose={() => setAddModal(null)}
              onSelect={handleRegisseurAdd}
              regisseurs={Regisseurs}
            />
          )}
        </Grid.Column>
        <Grid.Column width={1} />
      </Grid.Row>
      <Grid.Row>
        <Grid.Column>
          <Popup
            position="bottom left"
            mouseEnterDelay={1000}
            flowing
            trigger={
              <Form.Checkbox
                toggle
                name="regisseurConnexion"
                checked={regisseurConnexion}
                onChange={handleCheckboxChange}
                label="Demander une authentification supplémentaire lors de l'accès à la régie"
                data-testid="panel-regisseur-connexion-checkbox"
              />
            }
            content="Si cette option est active,
                l'utilisateur devra saisir son mot de passe une nouvelle fois
                pour pouvoir accéder aux fonctionnalités liées à cette régie."
          />
        </Grid.Column>
      </Grid.Row>
      <Button
        data-testid="add-button-regisseurs"
        basic
        icon="add"
        content="Ajouter un régisseur ou un mandataire"
        style={{
          position: 'absolute',
          top: -15,
          right: 0,
        }}
        onClick={() => setAddModal(true)}
      />
    </Grid>
  );
};

export default Regisseurs;
